import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, useSearchParams } from 'react-router'
import { useState, useEffect, useCallback } from 'react'
import {
  conversationQueries,
  messageQueries,
  conversationApi,
  messageApi,
  Conversation,
} from 'src/services/whatsapp'
import {
  ConversationList,
  ChatWindow,
  MessageComposer,
  NewConversationPanel,
  ChatOrgSelector,
} from 'src/components/chat'
import { TemplatesTab } from 'src/components/whatsapp-templates/templates-tab'
import { useAuth } from 'src/stores/auth'
import { SendPayload } from 'src/components/chat/template-composer'
import { usePagination } from 'src/hooks/use-pagination'
import { useDebouncedValue } from 'src/hooks/use-debounced-value'
import { PageHeader } from 'src/components/layout/page-header'
import { Button } from 'src/components/button'
import { useTranslation } from 'react-i18next'
import { FiMessageCircle } from 'react-icons/fi'
import { cn } from 'src/utils/style'

/**
 * `/chat-clients` — in-page tab toggle between Conversations (two-column chat
 * UI) and Templates (the retired `/whatsapp-templates` grid).
 * URL: /chat-clients, /chat-clients/:conversationId, /chat-clients?tab=templates
 */
const SEARCH_DEBOUNCE_MS = 400

type ChatClientsTab = 'conversations' | 'templates'

export const Chat = () => {
  const { t } = useTranslation()
  const { conversationId } = useParams<{ conversationId?: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  // A :conversationId in the URL always means the Conversations tab, even if a
  // stale ?tab=templates param is also present.
  const activeTab: ChatClientsTab =
    !conversationId && searchParams.get('tab') === 'templates' ? 'templates' : 'conversations'
  const setActiveTab = (tab: ChatClientsTab) => {
    setSearchParams(tab === 'templates' ? { tab } : {}, { replace: true })
  }
  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS)
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false)

  // The org the chat is scoped to is chosen explicitly via the card strip, not
  // implied by the global active-org switcher. Default to the active org when the
  // user belongs to it, otherwise fall back to their first membership.
  const activeOrgId = useAuth.use.activeOrgId()
  const memberships = useAuth.use.user()?.memberships ?? []
  const [selectedChatOrgId, setSelectedChatOrgId] = useState<string | null>(() => {
    if (activeOrgId && memberships.some((m) => m.organizationId === activeOrgId)) {
      return activeOrgId
    }
    return memberships[0]?.organizationId ?? null
  })

  const { pagination: conversationPagination, paginationCallback: conversationPaginationCallback } =
    usePagination()

  const { pagination: messagePagination, paginationCallback: messagePaginationCallback } =
    usePagination()

  const {
    data: conversationsData,
    isLoading: isLoadingConversations,
    isError: isConversationsError,
    error: conversationsError,
  } = useQuery(
    conversationQueries.list({
      // Scope the list to the org chosen in the selector, not every org at once.
      filters: {
        ...(selectedChatOrgId ? { organization: selectedChatOrgId } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      },
      pagination: conversationPagination,
      paginationCallback: conversationPaginationCallback,
    }),
  )

  const {
    data: selectedConversation,
    isLoading: isLoadingConversation,
    isError: isConversationError,
    error: conversationError,
  } = useQuery(conversationQueries.retrieve(conversationId || ''))

  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    isError: isMessagesError,
    error: messagesError,
  } = useQuery(
    messageQueries.list({
      filters: conversationId ? { conversation: conversationId } : undefined,
      pagination: messagePagination,
      paginationCallback: messagePaginationCallback,
    }),
  )

  const { mutate: markSeen } = useMutation({
    mutationFn: (id: string) => conversationApi.csc.markSeen(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueries.all() })
      if (conversationId) {
        queryClient.invalidateQueries({
          queryKey: conversationQueries.retrieve(conversationId).queryKey,
        })
      }
    },
  })

  const {
    mutate: sendMessage,
    isPending: isSendingMessage,
    isError: isSendMessageError,
    error: sendMessageError,
  } = useMutation({
    mutationFn: messageApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: messageQueries.list({
          filters: conversationId ? { conversation: conversationId } : undefined,
          pagination: messagePagination,
        }).queryKey,
      })
      queryClient.invalidateQueries({ queryKey: conversationQueries.all() })
      if (conversationId) {
        queryClient.invalidateQueries({
          queryKey: conversationQueries.retrieve(conversationId).queryKey,
        })
      }
    },
  })

  const {
    mutate: createConversation,
    isPending: isCreatingConversation,
    error: createConversationError,
    reset: resetCreateConversation,
  } = useMutation({
    // Opening a conversation is business-initiated, so the first message is sent
    // from an approved template — the team never composes it. Create the
    // conversation, then send that template as its opening message.
    mutationFn: async ({
      contactNumber,
      reservationId,
      contactName,
      contactLanguage,
      openingTemplateName,
      openingTemplateLanguage,
      openingTemplateVariables,
      ourNumber,
    }: {
      contactNumber: string
      reservationId?: string
      contactName?: string
      contactLanguage?: string
      openingTemplateName?: string
      openingTemplateLanguage?: string
      openingTemplateVariables?: Record<string, string>
      ourNumber?: string
    }) => {
      const conversation = await conversationApi.create({
        contactNumber,
        ...(ourNumber ? { ourNumber } : {}),
        ...(reservationId ? { reservation: reservationId } : {}),
        ...(contactName ? { contactName } : {}),
        ...(contactLanguage ? { contactLanguage } : {}),
      })
      if (openingTemplateName) {
        await messageApi.create({
          conversation: conversation.id,
          templateName: openingTemplateName,
          templateLanguage: openingTemplateLanguage,
          templateVariables: openingTemplateVariables,
        })
      }
      return conversation
    },
    onSuccess: (conversation) => {
      queryClient.invalidateQueries({ queryKey: conversationQueries.all() })
      setIsNewConversationOpen(false)
      navigate(`/chat-clients/${conversation.id}`)
    },
  })

  const handleSelectConversation = useCallback(
    (conversation: Conversation) => {
      navigate(`/chat-clients/${conversation.id}`)
    },
    [navigate],
  )

  const handleMarkSeen = useCallback(() => {
    if (conversationId) markSeen(conversationId)
  }, [conversationId, markSeen])

  // The composer decides free-form vs. template by window state and hands back
  // whichever payload that mode produces; the page only attaches the id.
  const handleSendMessage = useCallback(
    (payload: SendPayload) => {
      if (conversationId) sendMessage({ ...payload, conversation: conversationId })
    },
    [conversationId, sendMessage],
  )

  useEffect(() => {
    if (!conversationId) return
    const interval = setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: messageQueries.list({
          filters: { conversation: conversationId },
          pagination: messagePagination,
        }).queryKey,
      })
      queryClient.invalidateQueries({ queryKey: conversationQueries.all() })
    }, 5000)
    return () => clearInterval(interval)
  }, [conversationId, queryClient, messagePagination])

  const conversations = conversationsData?.results || []
  const messages = messagesData?.results || []

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      <PageHeader
        title={
          <>
            {activeTab === 'conversations' && (
              <FiMessageCircle className="inline h-6 w-6" aria-hidden="true" />
            )}
            {activeTab === 'conversations' ? t('chat.title') : t('whatsappTemplates.title')}
          </>
        }
      />

      {/* Same centered max width as MainContent on other pages */}
      <div className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col">
        <div role="tablist" className="flex gap-2 border-b border-border px-4">
          {(['conversations', 'templates'] as const).map((tab) => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              className={cn([
                '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-secondary hover:text-text-primary',
              ])}
              data-testid={`chat-tab-${tab}`}
            >
              {t(`chat.tabs.${tab}`)}
            </button>
          ))}
        </div>

        {activeTab === 'templates' ? (
          <TemplatesTab />
        ) : (
          <>
            {memberships.length > 0 && (
              <div className="border-b border-border px-4 py-3">
                <ChatOrgSelector
                  memberships={memberships}
                  selectedOrgId={selectedChatOrgId}
                  onSelect={setSelectedChatOrgId}
                />
              </div>
            )}
            <div className="flex flex-1 overflow-hidden">
              <div className="w-full md:w-80 lg:w-96">
                {isNewConversationOpen ? (
                  <NewConversationPanel
                    selectedOrgId={selectedChatOrgId}
                    onClose={() => {
                      setIsNewConversationOpen(false)
                      resetCreateConversation()
                    }}
                    onSubmit={(contactNumber, options) =>
                      createConversation({ contactNumber, ...options })
                    }
                    isLoading={isCreatingConversation}
                    error={
                      createConversationError instanceof Error ? createConversationError : null
                    }
                    onReviewTemplates={() => setActiveTab('templates')}
                  />
                ) : (
                  <ConversationList
                    conversations={conversations}
                    selectedConversationId={conversationId || null}
                    onSelectConversation={handleSelectConversation}
                    isLoading={isLoadingConversations}
                    isError={isConversationsError}
                    error={conversationsError}
                    searchTerm={searchInput}
                    onSearchChange={setSearchInput}
                    onNewConversation={() => {
                      resetCreateConversation()
                      setIsNewConversationOpen(true)
                    }}
                  />
                )}
              </div>

              <div className="hidden flex-1 md:flex">
                <div className="flex h-full w-full flex-col">
                  <ChatWindow
                    conversation={selectedConversation || null}
                    messages={messages}
                    isLoading={isLoadingMessages || isLoadingConversation}
                    isError={isMessagesError || isConversationError}
                    error={messagesError || conversationError}
                    onMarkSeen={handleMarkSeen}
                  />
                  {selectedConversation && (
                    <MessageComposer
                      conversation={selectedConversation}
                      onSend={handleSendMessage}
                      isLoading={isSendingMessage}
                      sendError={isSendMessageError ? (sendMessageError as Error) : null}
                    />
                  )}
                </div>
              </div>

              {conversationId && selectedConversation && (
                <div className="fixed inset-0 z-modal flex flex-col bg-surface md:hidden">
                  <div className="flex h-full w-full flex-col">
                    <ChatWindow
                      conversation={selectedConversation}
                      messages={messages}
                      isLoading={isLoadingMessages || isLoadingConversation}
                      isError={isMessagesError || isConversationError}
                      error={messagesError || conversationError}
                      onMarkSeen={handleMarkSeen}
                    />
                    <MessageComposer
                      conversation={selectedConversation}
                      onSend={handleSendMessage}
                      isLoading={isSendingMessage}
                      sendError={isSendMessageError ? (sendMessageError as Error) : null}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    shape="circle"
                    onClick={() => navigate('/chat-clients')}
                    className="absolute left-4 top-4 shadow-lg"
                    aria-label={t('common.back')}
                  >
                    {t('chat.back')}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
